import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { forwardRef } from 'react';
import styled from 'styled-components';
import { variant } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { theme } from '../../theme.js';
import { Flex } from '../Flex/index.js';
import { Link } from '../Link/index.js';

/* ======================================= */
const StyledNavBarItem = styled(Flex).withConfig({
  displayName: "NavBar__StyledNavBarItem",
  componentId: "sc-1rmbots-0"
})(["&:hover{&:before{opacity:1;}}&:before{position:absolute;content:' ';bottom:0px;width:100%;opacity:", ";transition:", ";transition-property:opacity;}&:focus{outline:0;}"], _ref => {
  let {
    selected
  } = _ref;
  return selected ? 1 : 0;
}, themeGet('transitions.fast'));
// Function to check if a color is present in the colors theme
const isColorInTheme = (color, obj) => {
  if (Object.prototype.hasOwnProperty.call(obj, color)) {
    return true;
  }
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    if (typeof value === 'object' && value !== null) {
      if (isColorInTheme(color, value)) {
        return true;
      }
    }
  }
  return false;
};
const StyledNavBar = styled(Flex).withConfig({
  shouldForwardProp: prop => {
    return !['underlineHeight', 'underlineColor'].includes(prop);
  }
}).withConfig({
  displayName: "NavBar__StyledNavBar",
  componentId: "sc-1rmbots-1"
})(["", " ", " + ", "{margin-left:", ";}", ":before{background-color:", ";height:", ";}"], variant({
  prop: 'placement',
  variants: {
    left: {
      justifyContent: 'flex-start'
    },
    center: {
      justifyContent: 'center'
    },
    right: {
      justifyContent: 'flex-end'
    },
    between: {
      justifyContent: 'space-between'
    }
  }
}), StyledNavBarItem, StyledNavBarItem, _ref2 => {
  let {
    placement,
    spacing
  } = _ref2;
  return placement === 'between' ? 'initial' : themeGet(`space.${spacing}`);
}, StyledNavBarItem, _ref3 => {
  let {
    underlineColor
  } = _ref3;
  // Check if the color is present in the colors theme
  // VM-2557: JOT workaround until context token theming is implemented
  const isColorToken = isColorInTheme(underlineColor, theme.colors);
  return isColorToken ? themeGet(`colors.${underlineColor}`) : underlineColor;
}, _ref4 => {
  let {
    underlineHeight
  } = _ref4;
  return `${underlineHeight}px`;
});
/* ======================================= */
/**
 * NavBar is used to identify major grouops of links used
 * for navigating through a website or page content.
 *
 * ### Documentation
 * - [Examples](https://blockparty.endpoint.com/components/navbar#usage)
 * - [Props](https://blockparty.endpoint.com/components/navbar#props)
 * <br />
 *
 */
const NavBar = /*#__PURE__*/forwardRef((_ref5, forwardedRef) => {
  let {
    children,
    dataTestId = 'nav-bar',
    spacing = 'space70',
    underlineHeight = 6,
    underlineColor = 'navbarUnderline',
    placement = 'left',
    as = 'nav',
    ...rest
  } = _ref5;
  return /*#__PURE__*/React__default.createElement(StyledNavBar, _extends({
    ref: forwardedRef,
    "data-test-id": dataTestId,
    direction: "row",
    forwardedAs: as,
    gap: spacing,
    height: "100%",
    placement: placement,
    underlineColor: underlineColor,
    underlineHeight: underlineHeight
  }, rest), children);
});
/**
 * NavBarLink is an individual link used in NavBar
 *
 * ### Documentation
 * - [Examples](https://blockparty.endpoint.com/components/navbar#usage)
 * - [Props](https://blockparty.endpoint.com/components/navbar#props)
 * <br />
 *
 */
const NavBarItem = /*#__PURE__*/forwardRef((_ref6, forwardedRef) => {
  let {
    children,
    dataTestId = 'nav-bar-item',
    selected = false,
    ...rest
  } = _ref6;
  return /*#__PURE__*/React__default.createElement(StyledNavBarItem, _extends({
    ref: forwardedRef,
    _focusVisible: {
      boxShadow: 'focus'
    },
    _hover: {
      textDecoration: 'none'
    },
    as: Link,
    borderRadius: "radiusDefault",
    color: "navbarLink",
    cursor: "pointer",
    "data-test-id": dataTestId,
    position: "relative",
    py: "space50",
    selected: selected,
    tabIndex: 0,
    transition: "normal",
    transitionProperty: "box-shadow",
    userSelect: "none"
  }, rest), children);
});

export { NavBar, NavBarItem };
